import React, { useState, useEffect } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Typography,
  Button,
  Divider,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Tooltip
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import formularioService from '../../services/formularioService';

/**
 * Componente mejorado para seleccionar formularios disponibles por especialidad
 * Incluye indicación de formularios PDF rellenables
 * @param {Object} props - Propiedades del componente
 * @param {number} props.especialidadId - ID de la especialidad
 * @param {number} props.historiaId - ID de la historia clínica
 * @param {function} props.onFormularioSelect - Función de callback al seleccionar un formulario
 * @returns {JSX.Element} Componente React
 */
const FormularioSelector = ({ especialidadId, historiaId, onFormularioSelect }) => {
  // Estados
  const [formularios, setFormularios] = useState([]);
  const [formulariosCompletados, setFormulariosCompletados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Cargar datos
  useEffect(() => {
    const cargarFormularios = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Cargar formularios por especialidad
        const response = await formularioService.getFormulariosPorEspecialidad(especialidadId);
        
        const formulariosData = Array.isArray(response.data) ? response.data : 
                               Array.isArray(response) ? response : [];
        
        setFormularios(formulariosData);
        
        // Cargar respuestas existentes para esta historia clínica
        try {
          const respuestasResponse = await formularioService.getRespuestasPorHistoria(historiaId);
          const respuestasData = Array.isArray(respuestasResponse.data) ? respuestasResponse.data : 
                                Array.isArray(respuestasResponse) ? respuestasResponse : [];
          
          // Extraer los IDs de formularios ya completados
          const idsCompletados = respuestasData.map(respuesta => respuesta.formulario_id);
          setFormulariosCompletados(idsCompletados);
        } catch (respuestasError) {
          console.error('Error al cargar respuestas:', respuestasError);
          setFormulariosCompletados([]);
        }
      } catch (error) {
        console.error('Error al cargar formularios:', error);
        setError('Error al cargar formularios. Por favor, intente nuevamente.');
        setFormularios([]);
      } finally {
        setLoading(false);
      }
    };
    
    if (especialidadId) {
      cargarFormularios();
    }
  }, [especialidadId, historiaId]);
  
  // Verificar si un formulario ya está completado
  const isFormularioCompletado = (formularioId) => {
    return formulariosCompletados.includes(formularioId);
  };
  
  // Manejar selección de FORMULARIO 
  const handleSelectFormulario = (formularioId) => {
  
  if (onFormularioSelect) {
 
    onFormularioSelect(formularioId);
  } else {
    console.error('onFormularioSelect no está definido');
  }
};
  
  // Si está cargando, mostrar indicador de progreso
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Si hay error, mostrar mensaje
  if (error) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        {error}
      </Alert>
    );
  }
  
  // Si no hay formularios, mostrar mensaje
  if (formularios.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="textSecondary">
          No hay formularios disponibles para esta especialidad.
        </Typography>
      </Paper>
    );
  }
  
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Formularios disponibles
      </Typography>
      <Divider sx={{ mb: 2 }} />
      
      <List>
        {formularios.map((formulario) => {
          const completado = isFormularioCompletado(formulario.id);
          const tienePdf = !!formulario.plantilla_pdf;
          
          return (
            <ListItem 
              key={formulario.id} 
              divider
              sx={{
                backgroundColor: completado ? 'rgba(76, 175, 80, 0.08)' : 'inherit'
              }}
            >
              <ListItemIcon>
                {tienePdf ? (
                  <Tooltip title="Formulario PDF rellenable">
                    <PdfIcon color={completado ? "success" : "primary"} />
                  </Tooltip>
                ) : (
                  <AssignmentIcon color={completado ? "success" : "primary"} />
                )}
              </ListItemIcon>
              <ListItemText 
                primary={formulario.nombre}
                secondary={
                  <>
                    {formulario.descripcion && (
                      <Typography variant="body2" color="textSecondary">
                        {formulario.descripcion}
                      </Typography>
                    )}
                    <Box mt={1}>
                      {tienePdf && (
                        <Chip 
                          icon={<PdfIcon />}
                          label="PDF Rellenable" 
                          size="small" 
                          color="secondary" 
                          variant="outlined"
                          sx={{ mr: 1 }}
                        />
                      )}
                      {completado && (
                        <Chip 
                          label="Completado" 
                          size="small" 
                          color="success" 
                          icon={<CheckCircleIcon />}
                        />
                      )}
                    </Box>
                  </>
                }
              />
              <ListItemSecondaryAction>
                <Button
                  variant="outlined"
                  color={completado ? "success" : "primary"}
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => handleSelectFormulario(formulario.id)}
                >
                  {completado ? 'Editar' : 'Completar'}
                </Button>
              </ListItemSecondaryAction>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
};

export default FormularioSelector;